import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { setCredentials, clearCredentials } from '@/store/authSlice';
import { setAccessToken } from '@/lib/axios';
import { authApi, type LoginPayload } from './auth.api';

/** Central auth hook: bootstraps session on load and exposes login/logout. */
export function useAuth() {
  const dispatch = useAppDispatch();
  const { user, status } = useAppSelector((s) => s.auth);
  const [bootstrapping, setBootstrapping] = useState(true);

  // On first load, try to restore a session via the refresh cookie.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { accessToken } = await authApi.refresh();
        setAccessToken(accessToken);
        const { user: me } = await authApi.me();
        if (active) dispatch(setCredentials({ user: me, accessToken }));
      } catch {
        if (active) dispatch(clearCredentials());
      } finally {
        if (active) setBootstrapping(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [dispatch]);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const { user: u, accessToken } = await authApi.login(payload);
      setAccessToken(accessToken);
      dispatch(setCredentials({ user: u, accessToken }));
      return u;
    },
    [dispatch],
  );

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => undefined);
    setAccessToken(null);
    dispatch(clearCredentials());
  }, [dispatch]);

  return { user, status, bootstrapping, login, logout, isAuthenticated: status === 'authenticated' };
}
